import React, { FC, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import { getLoginErrorText } from "Slices/appState/selectors";
import { completeNewPassword } from "Slices/appState";
import { validatePassword, checkPasswordRules } from "Utils/passwordValidation";
import { NewPasswordChallenge } from "Utils/cognito";

import {
  ForgotFormContainerStyled,
  TitleStyled,
  InputStyled,
  SubmitStyled,
  ErrMsgStyled,
  FormDescriptionStyled,
  PasswordRequirementsStyled,
  RequirementItemStyled,
} from "./styles";

type SetNewPasswordProps = {
  challenge: NewPasswordChallenge;
};

export const SetNewPasswordComponent: FC<SetNewPasswordProps> = ({
  challenge,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loginErrorText = useSelector(getLoginErrorText);

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const rules = checkPasswordRules(newPassword);

  const handleSubmit = (ev: React.FormEvent<HTMLFormElement>) => {
    ev.preventDefault();
    setError(null);

    if (!newPassword || !confirmPassword) {
      setError("Please fill in both password fields.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    const validationError = validatePassword(newPassword);
    if (validationError) {
      setError(validationError);
      return;
    }

    dispatch(
      completeNewPassword({
        session: challenge.session,
        email: challenge.email,
        newPassword,
        navigateCallBack: navigate,
      })
    );
  };

  return (
    <ForgotFormContainerStyled onSubmit={handleSubmit}>
      <TitleStyled>Set Your Password</TitleStyled>
      <FormDescriptionStyled>
        Welcome! Please create a password for your account.
      </FormDescriptionStyled>

      {(error || loginErrorText) && (
        <ErrMsgStyled>{error || loginErrorText}</ErrMsgStyled>
      )}

      <InputStyled
        type="password"
        name="newPassword"
        onChange={(e) => setNewPassword(e.currentTarget.value)}
        placeholder="New Password"
        value={newPassword}
      />
      <InputStyled
        type="password"
        name="confirmPassword"
        onChange={(e) => setConfirmPassword(e.currentTarget.value)}
        placeholder="Confirm Password"
        value={confirmPassword}
      />

      <PasswordRequirementsStyled>
        <RequirementItemStyled met={rules.length}>
          At least 12 characters
        </RequirementItemStyled>
        <RequirementItemStyled met={rules.uppercase}>
          One uppercase letter
        </RequirementItemStyled>
        <RequirementItemStyled met={rules.lowercase}>
          One lowercase letter
        </RequirementItemStyled>
        <RequirementItemStyled met={rules.number}>
          One number
        </RequirementItemStyled>
        <RequirementItemStyled met={rules.special}>
          One special character
        </RequirementItemStyled>
      </PasswordRequirementsStyled>

      <SubmitStyled>SET PASSWORD</SubmitStyled>
    </ForgotFormContainerStyled>
  );
};
